import React, { useState, useEffect, useCallback } from "react";
import Sidebar, { SIDEBAR_WIDTH } from "./Sidebar";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { supabase } from "./supabaseClient";

export default function TeacherDashboardLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarVisible, setSidebarVisible] = useState(() => {
    const saved = localStorage.getItem('sidebarVisible');
    const isMobileCheck = window.innerWidth <= 768;
    // On mobile, default to hidden; on desktop, use saved state or default to open
    if (isMobileCheck) return false;
    return saved !== null ? JSON.parse(saved) : true;
  });
  // Initialize language from localStorage to stay consistent
  const [isArabic, setIsArabic] = useState(() => {
    const savedLang = localStorage.getItem('taealam_language');
    return savedLang === 'ar';
  });
  const [checking, setChecking] = useState(true);
  const [profileIncomplete, setProfileIncomplete] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  
  // Cache teacher profile for use across components
  const [cachedTeacherProfile, setCachedTeacherProfile] = useState(null);
  const [teacherProfileLoading, setTeacherProfileLoading] = useState(true);
  
  // Define fetchTeacherProfile at component level so it can be called from useEffect and refreshTeacherProfile
  const fetchTeacherProfile = useCallback(async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setTeacherProfileLoading(false);
        return;
      }

      // Fetch complete teacher profile
      const { data: teacher, error } = await supabase
        .from('Teachers')
        .select('*')
        .eq('email', user.email)
        .single();

      if (!error && teacher) {
        setCachedTeacherProfile(teacher);
        console.log('[Layout] Teacher profile cached:', teacher.full_name);
      }

    } catch (error) {
      console.error('[Layout] Error fetching teacher profile:', error);
    } finally {
      setTeacherProfileLoading(false);
    }
  }, []); // Empty deps since we want a stable reference

  // Fetch teacher profile once and cache it
  useEffect(() => {
    fetchTeacherProfile();
  }, [fetchTeacherProfile]); // Run once on mount

  // Handle window resize for mobile detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Check if teacher has completed confirmation form and profile
  useEffect(() => {
    const checkConfirmationAndProfile = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        
        if (!user) {
          navigate('/signin');
          return;
        }

        const currentPath = location.pathname;
        const isProfilePage = currentPath === '/dashboard/teacher/profile';

        // Check if teacher has an approved submission from the wizard
        const { data: submissionData, error: submissionError } = await supabase
          .from('teacher_onboarding_submissions')
          .select('status')
          .eq('teacher_id', user.id)
          .order('submitted_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        // If no submission or submission is not approved, redirect to wizard
        if (!submissionData || submissionData.status !== 'approved') {
          if (submissionData?.status === 'rejected') {
            // Show message that application was rejected
            alert('Your teacher application has been rejected. Please contact support if you have questions.');
          }
          navigate('/teacher/signup', { replace: true });
          return;
        }

        // Check if teacher has completed confirmation and profile (using email since id is BIGINT)
        const { data: teacherData, error: teacherError } = await supabase
          .from('Teachers')
          .select('confirmation_completed, profile_photo, bio, subjects, grade_levels, languages, hourly_rate, lesson_format_preference')
          .eq('email', user.email)
          .single();

        if (teacherError) {
          console.error('Error fetching teacher data:', teacherError);
          setChecking(false);
          return;
        }

        // If confirmation not completed, redirect to form
        if (!teacherData?.confirmation_completed) {
          navigate('/teacher-confirm', { replace: true });
          return;
        }

        // Check if profile is complete
        // Required fields: profile_photo (image), bio, subjects (Teaching Details), grade_levels (Teaching Details), languages (Teaching Details), hourly_rate (Pricing)
        const isProfileComplete = !!(
          teacherData?.profile_photo &&
          teacherData?.bio &&
          teacherData?.bio.trim().length > 0 &&
          teacherData?.subjects &&
          Array.isArray(teacherData.subjects) &&
          teacherData.subjects.length > 0 &&
          teacherData?.grade_levels &&
          Array.isArray(teacherData.grade_levels) &&
          teacherData.grade_levels.length > 0 &&
          teacherData?.languages &&
          Array.isArray(teacherData.languages) &&
          teacherData.languages.length > 0 &&
          teacherData?.hourly_rate &&
          teacherData.hourly_rate > 0 &&
          teacherData?.lesson_format_preference
        );

        // If profile not complete and not on profile page, show message
        if (!isProfileComplete && !isProfilePage) {
          setChecking(false);
          setProfileIncomplete(true);
          return;
        }

        // Both confirmation and profile completed, allow access
        setProfileIncomplete(false);
        setChecking(false);
      } catch (err) {
        console.error('Error checking confirmation and profile:', err);
        setChecking(false);
      }
    };

    checkConfirmationAndProfile();
  }, [navigate, location]);

  // Persist sidebar state when it changes
  React.useEffect(() => {
    localStorage.setItem('sidebarVisible', JSON.stringify(sidebarVisible));
  }, [sidebarVisible]);

  // Handler for language change from Sidebar
  const handleLanguageChange = (arabic) => {
    console.log('TeacherDashboardLayout: handleLanguageChange called with:', arabic);
    console.log('TeacherDashboardLayout: Current isArabic before change:', isArabic);
    setIsArabic(arabic);
    // Save to localStorage to persist across pages
    localStorage.setItem('taealam_language', arabic ? 'ar' : 'en');
    // Update document direction
    document.documentElement.dir = arabic ? 'rtl' : 'ltr';
    document.documentElement.lang = arabic ? 'ar' : 'en';
    console.log('TeacherDashboardLayout: isArabic state updated to:', arabic);
  };

  // Show loading while checking confirmation
  if (checking) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#FDF8EE'
      }}>
        <div style={{
          textAlign: 'center',
          color: '#0F2545'
        }}>
          <div className="spin" style={{
            width: 40,
            height: 40,
            border: '4px solid rgba(15, 37, 69, 0.1)',
            borderTopColor: '#0F2545',
            borderRadius: '50%',
            margin: '0 auto 20px'
          }} />
          <div>Loading...</div>
        </div>
      </div>
    );
  }

  // Show message if profile is incomplete
  if (profileIncomplete) {
    const messagesAr = {
      title: 'الملف الشخصي غير مكتمل',
      items: [
        { icon: '📷', text: 'صورة الملف الشخصي' },
        { icon: '✍️', text: 'نبذة عنك' },
        { icon: '📚', text: 'تفاصيل التدريس (المواد، المستويات، اللغات)' },
        { icon: '💰', text: 'التسعير (السعر للساعة)' }
      ],
      buttonText: 'الذهاب إلى صفحة الملف الشخصي',
      howTo: 'كيفية الإكمال:',
      steps: [
        'انقر على الزر أدناه للانتقال إلى صفحة الملف الشخصي',
        'املأ جميع الحقول المطلوبة',
        'احفظ التغييرات'
      ]
    };
    
    const messagesEn = {
      title: 'Profile Incomplete',
      items: [
        { icon: '📷', text: 'Profile Image' },
        { icon: '✍️', text: 'Bio' },
        { icon: '📚', text: 'Teaching Details (Subjects, Grade Levels, Languages)' },
        { icon: '💰', text: 'Pricing (Hourly Rate)' }
      ],
      buttonText: 'Go to Profile Page',
      howTo: 'How to complete:',
      steps: [
        'Click the button below to go to your profile page',
        'Fill in all required fields',
        'Save your changes'
      ]
    };

    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #FDF8EE 0%, #FFF4E6 50%, #FDF8EE 100%)',
        fontFamily: "'IBM Plex Sans Arabic', sans-serif",
        padding: 20,
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Decorative background elements */}
        <div style={{
          position: 'absolute',
          top: -100,
          right: -100,
          width: 400,
          height: 400,
          borderRadius: '50%',
          background: 'rgba(255, 116, 38, 0.05)',
          zIndex: 0
        }} />
        <div style={{
          position: 'absolute',
          bottom: -150,
          left: -150,
          width: 500,
          height: 500,
          borderRadius: '50%',
          background: 'rgba(15, 37, 69, 0.03)',
          zIndex: 0
        }} />
        
        <div style={{
          background: '#fff',
          borderRadius: 32,
          padding: 48,
          boxShadow: '0 20px 60px rgba(15, 37, 69, 0.15)',
          textAlign: 'center',
          maxWidth: 800,
          border: '1px solid rgba(15, 37, 69, 0.08)',
          position: 'relative',
          zIndex: 1,
          overflow: 'hidden'
        }}>
          {/* Decorative top accent */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: 6,
            background: 'linear-gradient(90deg, #FF7426 0%, #FF8C42 50%, #FF7426 100%)'
          }} />
          
          {/* Icon with background */}
          <div style={{
            width: 100,
            height: 100,
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #FFF4E6 0%, #FFE5D1 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 24px',
            fontSize: 48,
            boxShadow: '0 8px 24px rgba(255, 116, 38, 0.2)'
          }}>
            ✏️
          </div>
          
          {/* Title Section */}
          <div style={{ marginBottom: 40 }}>
            <h2 style={{
              fontSize: 32,
              fontWeight: 800,
              color: '#0F2545',
              marginBottom: 12,
              lineHeight: 1.2,
              background: 'linear-gradient(135deg, #0F2545 0%, #1a3a5c 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}>
              {messagesAr.title}
            </h2>
            <h2 style={{
              fontSize: 32,
              fontWeight: 800,
              color: '#0F2545',
              marginBottom: 0,
              lineHeight: 1.2
            }}>
              {messagesEn.title}
            </h2>
          </div>
          
          {/* Required Items - Modern Card Design */}
          <div style={{
            background: 'linear-gradient(135deg, #FDF8EE 0%, #FFF9F0 100%)',
            borderRadius: 20,
            padding: 32,
            marginBottom: 32,
            border: '2px solid #FFE5D1',
            boxShadow: '0 4px 16px rgba(255, 116, 38, 0.08)'
          }}>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 16
            }}>
              {messagesAr.items.map((itemAr, idx) => {
                const itemEn = messagesEn.items[idx];
                return (
                  <div key={idx} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 20,
                    padding: 16,
                    borderRadius: 12,
                    background: idx % 2 === 0 ? 'rgba(255, 255, 255, 0.6)' : 'transparent',
                    transition: 'all 0.2s'
                  }}>
                    {/* Single Icon */}
                    <div style={{
                      fontSize: 32,
                      lineHeight: 1,
                      flexShrink: 0
                    }}>
                      {itemAr.icon}
                    </div>
                    {/* Bilingual Text */}
                    <div style={{
                      flex: 1,
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      gap: 16
                    }}>
                      <span style={{
                        color: '#0F2545',
                        fontSize: 15,
                        fontWeight: 600,
                        textAlign: 'right',
                        direction: 'rtl',
                        flex: 1
                      }}>
                        {itemAr.text}
                      </span>
                      <span style={{
                        color: '#0F2545',
                        fontSize: 15,
                        fontWeight: 600,
                        textAlign: 'left',
                        direction: 'ltr',
                        flex: 1
                      }}>
                        {itemEn.text}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          
          {/* How to Complete - Modern Steps */}
          <div style={{
            background: '#f8fafc',
            borderRadius: 20,
            padding: 32,
            marginBottom: 32,
            border: '1px solid #e2e8f0'
          }}>
            <div style={{
              fontSize: 18,
              fontWeight: 700,
              color: '#0F2545',
              marginBottom: 24,
              textAlign: 'center'
            }}>
              {messagesAr.howTo} / {messagesEn.howTo}
            </div>
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 24
            }}>
              {/* Arabic Steps */}
              <div style={{ textAlign: 'right', direction: 'rtl' }}>
                {messagesAr.steps.map((step, idx) => (
                  <div key={idx} style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 16,
                    marginBottom: 16,
                    padding: 16,
                    borderRadius: 12,
                    background: '#fff',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                    transition: 'all 0.2s'
                  }}>
                    <div style={{
                      width: 32,
                      height: 32,
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #FF7426 0%, #FF8C42 100%)',
                      color: '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 14,
                      fontWeight: 700,
                      flexShrink: 0,
                      boxShadow: '0 4px 12px rgba(255, 116, 38, 0.3)'
                    }}>
                      {idx + 1}
                    </div>
                    <span style={{
                      flex: 1,
                      color: '#374151',
                      fontSize: 14,
                      lineHeight: 1.6,
                      fontWeight: 500
                    }}>
                      {step}
                    </span>
                  </div>
                ))}
              </div>
              {/* English Steps */}
              <div style={{ textAlign: 'left', direction: 'ltr' }}>
                {messagesEn.steps.map((step, idx) => (
                  <div key={idx} style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 16,
                    marginBottom: 16,
                    padding: 16,
                    borderRadius: 12,
                    background: '#fff',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                    transition: 'all 0.2s'
                  }}>
                    <div style={{
                      width: 32,
                      height: 32,
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #FF7426 0%, #FF8C42 100%)',
                      color: '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 14,
                      fontWeight: 700,
                      flexShrink: 0,
                      boxShadow: '0 4px 12px rgba(255, 116, 38, 0.3)'
                    }}>
                      {idx + 1}
                    </div>
                    <span style={{
                      flex: 1,
                      color: '#374151',
                      fontSize: 14,
                      lineHeight: 1.6,
                      fontWeight: 500
                    }}>
                      {step}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
          
          {/* CTA Button */}
          <button
            onClick={() => navigate('/dashboard/teacher/profile', { replace: true })}
            style={{
              width: '100%',
              background: 'linear-gradient(135deg, #FF7426 0%, #FF8C42 100%)',
              border: 'none',
              color: '#fff',
              padding: '18px 40px',
              borderRadius: 16,
              fontSize: 17,
              fontWeight: 700,
              cursor: 'pointer',
              boxShadow: '0 8px 24px rgba(255, 116, 38, 0.35)',
              transition: 'all 0.3s',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 12,
              letterSpacing: '0.3px'
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-3px)';
              e.target.style.boxShadow = '0 12px 32px rgba(255, 116, 38, 0.45)';
              e.target.style.background = 'linear-gradient(135deg, #FF8C42 0%, #FF7426 100%)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 8px 24px rgba(255, 116, 38, 0.35)';
              e.target.style.background = 'linear-gradient(135deg, #FF7426 0%, #FF8C42 100%)';
            }}
          >
            <span>{messagesAr.buttonText} / {messagesEn.buttonText}</span>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className={isArabic ? 'arabic-font' : ''}
      style={{ background: "#f7f9fb", minHeight: "100vh", width: "100%", position: 'relative' }}
      dir={isArabic ? 'rtl' : 'ltr'}
    >
      {/* Hamburger button to toggle sidebar */}
      {/* On mobile: hide close button, show smaller hamburger button without white background */}
      {(!isMobile || !sidebarVisible) && (
        <button
          aria-label={sidebarVisible ? (isArabic ? "إخفاء القائمة" : "Hide sidebar") : (isArabic ? "إظهار القائمة" : "Show sidebar")}
          onClick={() => setSidebarVisible((v) => !v)}
          style={{
            position: 'fixed',
            top: isMobile ? 12 : 18,
            left: isArabic ? 'auto' : (isMobile ? 12 : 18),
            right: isArabic ? (isMobile ? 12 : 18) : 'auto',
            zIndex: 2100,
            background: isMobile ? 'transparent' : '#fff',
            border: 'none',
            borderRadius: isMobile ? 0 : 8,
            boxShadow: isMobile ? 'none' : '0 2px 8px #eee',
            width: isMobile ? 28 : 44,
            height: isMobile ? 28 : 44,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            padding: 0,
          }}
        >
          {sidebarVisible ? (
            // Close (X) icon - hidden on mobile
            <svg width={isMobile ? 20 : 28} height={isMobile ? 20 : 28} viewBox="0 0 24 24" fill="none" stroke="#232B3E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
          ) : (
            // Hamburger icon - smaller on mobile, no background
            <svg width={isMobile ? 18 : 28} height={isMobile ? 18 : 28} viewBox="0 0 24 24" fill="none" stroke="#232B3E" strokeWidth={isMobile ? "2.5" : "2"} strokeLinecap="round" strokeLinejoin="round"><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="18" x2="21" y2="18" /></svg>
          )}
        </button>
      )}
      {sidebarVisible && (
        <Sidebar
          userRole="teacher"
          isVisible={sidebarVisible}
          isOpen={true}
          setSidebarVisible={setSidebarVisible}
          isArabic={isArabic}
          onLanguageChange={handleLanguageChange}
          cachedUserProfile={cachedTeacherProfile}
        />
      )}
      <main
        className="p-4 xs:p-6 sm:p-8 md:p-10 lg:p-12
                   transition-all duration-300"
        style={{
          flex: 1,
          marginLeft: !isArabic && sidebarVisible && !isMobile ? SIDEBAR_WIDTH : 0,
          marginRight: isArabic && sidebarVisible && !isMobile ? SIDEBAR_WIDTH : 0,
        }}
      >
        <Outlet context={{ 
          sidebarVisible, 
          setSidebarVisible, 
          isArabic, 
          handleLanguageChange,
          // OPTIMIZATION: Share cached teacher profile across all child routes
          cachedTeacherProfile,
          teacherProfileLoading,
          // Function to update cached profile if needed
          refreshTeacherProfile: () => {
            setTeacherProfileLoading(true);
            fetchTeacherProfile();
          }
        }} />
      </main>
    </div>
  );
} 